import { useEffect, useState, useMemo } from "react";
import { GOAL_ROTATION_INTERVAL, formatDate, getGoalKey } from "../../constants-ticker";
import Team from "./resultContentParts/team";
import { GoalRotator } from "./resultContentParts/goalRotator";
import ResultTeamRow from "./resultContentParts/resultTeamRow";

const getTeamId = (result, soccerData, side) => {
  const isHome = side === "home";
  const keys = isHome
    ? ["id_local", "idLocal", "localId", "home_id", "homeId", "left_id", "leftId"]
    : ["id_visita", "idVisita", "visitId", "away_id", "awayId", "right_id", "rightId"];
  const team = isHome
    ? result.local ?? result.home ?? result.homeTeam ?? result.left
    : result.visita ?? result.away ?? result.awayTeam ?? result.right;

  return keys.map((key) => soccerData[key] ?? result[key]).find((id) => id != null) ?? team?.id ?? team?.contestantId;
};

const getSoccerGoals = (result) => {
  const soccerData = result.SoccerData ?? result.soccerData ?? result.soccer_data ?? {};
  const rawGoals = soccerData.goals ?? soccerData.goal ?? result.goals ?? result.goal ?? [];
  const goals = Array.isArray(rawGoals) ? rawGoals : [rawGoals].filter(Boolean);
  const localId = getTeamId(result, soccerData, "home");
  const visitId = getTeamId(result, soccerData, "away");
  const home = [];
  const away = [];
  const unassigned = [];

  goals.forEach((goal) => {
    const contestantId = goal?.contestantId ?? goal?.constantId ?? goal?.contestant_id ?? goal?.constant_id;

    if (localId != null && String(contestantId) === String(localId)) {
      home.push(goal);
    } else if (visitId != null && String(contestantId) === String(visitId)) {
      away.push(goal);
    } else {
      unassigned.push(goal);
    }
  });

  return { home, away, unassigned };
};

/** Tarjeta de fútbol horizontal para resultados y próximos encuentros. */
export default function TickerGlobalResultCard({ result = {}, league = {}, sportCode }) {
  const isNext = result.class === "clarosports-next-game";
  const isEnd = result.class === "clarosports-finished-game";
  const isProgress = result.class === "clarosports-in-progress-game";
  const isPlaying = ["clarosports-finished-game", "clarosports-in-progress-game"].includes(result.class);
  const isFoot = sportCode === "FOOT";
  const getScoreClass = (baseClass, teamClass) =>
    `${baseClass} ${["clarosports-winner", "clarosports-draw"].includes(teamClass) ? "score-ganador" : "score-perdedor"}`;
  const teams = {
    home: {
      name: result.left_short_name || result.left_name || "Local",
      nameLarge: result.left_name || "Local",
      image: result.left_image,
      score: result.left_score,
      scoreClass: result.left_class,
    },
    away: {
      name: result.right_short_name || result.right_name || "Visita",
      nameLarge: result.right_name || "Visita",
      image: result.right_image,
      score: result.right_score,
      scoreClass: result.right_class,
    },
  };
  const hasScoreDetail = isNext || isProgress || isEnd;
  const scoreDetailLabel = isEnd ? "Finalizado" : isProgress ? result.period_title : formatDate(result.utc_date);
  const soccerGoals = useMemo(
    () => hasScoreDetail ? getSoccerGoals(result) : null,
    [hasScoreDetail, result]
  );
  const hasGoals = Boolean(soccerGoals && (
    soccerGoals.home.length || soccerGoals.away.length || soccerGoals.unassigned.length
  ));
  const [goalIndex, setGoalIndex] = useState(0);
  const goalRotationKey = soccerGoals
    ? [...soccerGoals.home, ...soccerGoals.away, ...soccerGoals.unassigned].map(getGoalKey).join("|")
    : "";
  const shouldRotateGoals = isFoot && hasGoals && [
    soccerGoals.home.length,
    soccerGoals.away.length,
    soccerGoals.unassigned.length,
  ].some((goalCount) => goalCount > 1);

  useEffect(() => {
    setGoalIndex(0);
    if (!shouldRotateGoals) return undefined;

    const interval = window.setInterval(() => {
      setGoalIndex((currentIndex) => currentIndex + 1);
    }, GOAL_ROTATION_INTERVAL);

    return () => window.clearInterval(interval);
  }, [goalRotationKey, shouldRotateGoals]);

  if (isFoot) {
    const cardClasses = [
      "ticker-result-card",
      "ticker-result-card--global",
      "ticker-result-card--foot",
      isNext && "ticker-result-card--upcoming",
      isProgress && "ticker-result-card--in-progress",
      isEnd && "ticker-result-card--finished",
      hasScoreDetail && "ticker-result-card--with-goals",
    ].filter(Boolean).join(" ");

    return (
      <article className={cardClasses}>
        <div className="ticker-foot-card__match">
          <Team team={teams.home} side="home" type="results" />

          <div className="ticker-foot-card__score-stack">
            {isPlaying ? (
              <>
                <span className={getScoreClass("ticker-foot-card__score", teams.home.scoreClass)}>
                  {teams.home.score}
                </span>
                <span className="ticker-foot-card__score">-</span>
                <span className={getScoreClass("ticker-foot-card__score", teams.away.scoreClass)}>
                  {teams.away.score}
                </span>
              </>
            ) : (
              <span className="ticker-foot-card__score">VS</span>
            )}
          </div>
          <Team team={teams.away} side="away" type="results" />
        </div>
        {hasScoreDetail && (
          <div className="ticker-foot-card__goals">
            {hasGoals ? (
              <>
                <GoalRotator
                  goals={soccerGoals.home}
                  goalIndex={goalIndex}
                  className="ticker-foot-card__goals--home"
                  side="home"
                />
                {soccerGoals.unassigned.length ? (
                  <GoalRotator
                    goals={soccerGoals.unassigned}
                    goalIndex={goalIndex}
                    className="ticker-foot-card__minute"
                  />
                ) : (
                  <span className="ticker-foot-card__minute">
                    {scoreDetailLabel}
                  </span>
                )}
                <GoalRotator
                  goals={soccerGoals.away}
                  goalIndex={goalIndex}
                  className="ticker-foot-card__goals--away"
                  side="away"
                />
              </>
            ) : (
              <>
                <span />
                <span className="ticker-foot-card__minute">{scoreDetailLabel}</span>
                <span />
              </>
            )}
          </div>
        )}
      </article>
    );
  }

  return (
    <article className="ticker-result-card ticker-result-card--global">
      <header className="ticker-result-card__header">
        <span>{isNext ? formatDate(result.utc_date) : result.period_title || "Final"}</span>
      </header>
      <div className="ticker-result-card__body">
        {[teams.home, teams.away].map((team, index) => (
          <ResultTeamRow
            key={`${team.name}-${index}`}
            team={team}
            score={isNext ? "-" : team.score ?? "-"}
          />
        ))}
      </div>
    </article>
  );
}